import {
  JAPAN_BOUNDS,
  MIN_SEARCH_RADIUS,
  MAX_SEARCH_RADIUS,
  SEARCH_PAN_THRESHOLD,
  SEARCH_ZOOM_IN_THRESHOLD,
  SEARCH_ZOOM_OUT_THRESHOLD,
} from './map-constants'

interface ILatLng {
  lat: number
  lng: number
}

interface ISearchState {
  center: ILatLng
  radius: number
}

const R_EARTH = 6_371_000 // Earth radius in meters

/** Haversine formula: returns distance in meters between two points */
export function haversineDistance(a: ILatLng, b: ILatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const sinHalfLat = Math.sin(dLat / 2)
  const sinHalfLng = Math.sin(dLng / 2)
  const h = sinHalfLat * sinHalfLat + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinHalfLng * sinHalfLng
  return Math.round(2 * R_EARTH * Math.asin(Math.sqrt(h)))
}

/** Check if coordinates fall within Japan's bounding box */
export function isInJapan(lat: number, lng: number): boolean {
  return (
    lat >= JAPAN_BOUNDS.latMin && lat <= JAPAN_BOUNDS.latMax &&
    lng >= JAPAN_BOUNDS.lngMin && lng <= JAPAN_BOUNDS.lngMax
  )
}

/** Compute a search radius from the map's current bounds (center → NE corner distance) */
export function computeViewportRadius(map: google.maps.Map): number {
  const bounds = map.getBounds()
  if (!bounds) return 3000

  const center = bounds.getCenter()
  const ne = bounds.getNorthEast()
  const raw = haversineDistance(
    { lat: center.lat(), lng: center.lng() },
    { lat: ne.lat(), lng: ne.lng() },
  )
  return Math.max(MIN_SEARCH_RADIUS, Math.min(MAX_SEARCH_RADIUS, raw))
}

/** Unified metric: should we show the "Search this area" button? */
export function shouldShowSearchButton(old: ISearchState, current: ISearchState): boolean {
  const displacement = haversineDistance(old.center, current.center)
  if (displacement > SEARCH_PAN_THRESHOLD * old.radius) return true
  if (current.radius < SEARCH_ZOOM_IN_THRESHOLD * old.radius) return true
  if (current.radius > SEARCH_ZOOM_OUT_THRESHOLD * old.radius) return true
  return false
}

/** Parse location from multiple possible database formats (Array or Object) */
export function parseLocation(loc: any): ILatLng | null {
  if (!loc) return null
  if (Array.isArray(loc)) {
    // location[0] = latitude, location[1] = longitude
    const lat = Number(loc[0])
    const lng = Number(loc[1])
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng }
    }
  } else if (typeof loc === 'object') {
    if ('lat' in loc && 'lng' in loc) {
      const lat = Number(loc.lat)
      const lng = Number(loc.lng)
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng }
      }
    } else if ('y' in loc && 'x' in loc) {
      // In this project, DB point stores latitude in x and longitude in y
      // Let's verify: Tokyo latitude is ~35, longitude is ~139.
      const val1 = Number(loc.x)
      const val2 = Number(loc.y)
      if (!isNaN(val1) && !isNaN(val2)) {
        // If x is around 35 (latitude) and y is around 139 (longitude)
        if (Math.abs(val1) < 90 && Math.abs(val2) > 90) {
          return { lat: val1, lng: val2 }
        } else if (Math.abs(val2) < 90 && Math.abs(val1) > 90) {
          return { lat: val2, lng: val1 }
        } else {
          // Fallback to project standard where x is lat, y is lng
          return { lat: val1, lng: val2 }
        }
      }
    }
  }
  return null
}

